import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { CustomDropdown } from '../deps.js';
import AdminModal from '../AdminModal.js';
import { parseBulkUserList, partitionBulkUsers } from './parseBulkUserList.js';

const BulkAddUsersModal = ({ isOpen, onClose, onSubmit, roles, loading, existingUserEmails }) => {
  const [userListText, setUserListText] = useState('');
  const [role, setRole] = useState('');
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setUserListText('');
      setRole('');
      setRoleDropdownOpen(false);
    }
  }, [isOpen]);

  const { parsed, errors } = useMemo(() => parseBulkUserList(userListText), [userListText]);
  const { usersToAdd, alreadyPresent } = useMemo(
    () => partitionBulkUsers(parsed, existingUserEmails),
    [parsed, existingUserEmails]
  );
  const roleTitle = role ? (roles[role]?.title || role) : null;
  const canSubmit = usersToAdd.length > 0 && role !== '' && errors.length === 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(usersToAdd, role);
  };

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add users"
      containerClassName="bulk-add-users-modal"
      actions={(
        <>
          <button className="admin-modal-cancel-button" onClick={onClose}>Cancel</button>
          <button
            className="admin-submit-button"
            onClick={handleSubmit}
            disabled={loading || !canSubmit}
          >
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              `Add ${usersToAdd.length || 0} user${usersToAdd.length !== 1 ? 's' : ''}`
            )}
          </button>
        </>
      )}
    >
      <p className="bulk-add-users-description">
        Paste a list of names and emails to add multiple users at once
      </p>
      <form onSubmit={handleSubmit}>
        <div className="admin-form-group">
          <label>Default role for all users</label>
          <CustomDropdown
            selected={role ? roleTitle : 'Select Role'}
            options={
              Object.entries(roles)
                .map(([roleId, roleData]) => ({
                  id: roleId,
                  display: roleData.title || roleId,
                  value: roleId,
                }))
                .sort((a, b) => a.display.localeCompare(b.display))
            }
            isOpen={roleDropdownOpen}
            toggleDropdown={() => setRoleDropdownOpen(!roleDropdownOpen)}
            onSelect={(option) => {
              setRole(option.value);
              setRoleDropdownOpen(false);
            }}
            className="admin-role-select"
            containerRef={roleDropdownRef}
            keyExtractor={(option) => option.id}
          />
        </div>
        <div className="admin-form-group">
          <label htmlFor="bulkUserList">
            Paste user list <span className="bulk-add-users-hint">one per line, as: Full Name, email@address.com</span>
          </label>
          <textarea
            id="bulkUserList"
            className="bulk-add-users-textarea"
            value={userListText}
            onChange={(e) => setUserListText(e.target.value)}
            placeholder={'Dr Sarah Ahmed, sahmed@chingfordmedical.nhs.net\nDr Tom Reeves, treeves@chingfordmedical.nhs.net\nPriya Nair, pnair@chingfordmedical.nhs.net'}
            rows={8}
          />
        </div>
      </form>
      {userListText.trim() !== '' && (
        <div className="bulk-add-users-status-container">
          {errors.length > 0 ? (
            <div className="bulk-add-users-status bulk-add-users-status-error">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>
                {errors.length} line{errors.length !== 1 ? 's' : ''} could not be parsed — check format: Full Name, email@address.com
              </span>
            </div>
          ) : (
            <>
              {usersToAdd.length > 0 && (
                <div className="bulk-add-users-status">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>
                    {usersToAdd.length} user{usersToAdd.length !== 1 ? 's' : ''} ready to add{roleTitle ? ` with role ${roleTitle}` : ''}
                  </span>
                </div>
              )}
              {alreadyPresent.length > 0 && (
                <div className="bulk-add-users-status bulk-add-users-status-warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <div>
                    <span>
                      {alreadyPresent.length} already present — will be skipped:
                    </span>
                    <ul className="bulk-add-users-existing-list">
                      {alreadyPresent.map((email) => (
                        <li key={email}>{email}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {usersToAdd.length === 0 && parsed.length > 0 && (
                <div className="bulk-add-users-status bulk-add-users-status-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>All parsed users are already present</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </AdminModal>
  );
};

export default BulkAddUsersModal;
